<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <div class="page-header">
          <b-breadcrumb class="m-0">
            <b-breadcrumb-item
              :to="{
                name: 'DashboardHome',
              }"
            >
              <i class="fa fa-home"></i>
            </b-breadcrumb-item>
            <b-breadcrumb-item active>
              參數設定 
            </b-breadcrumb-item>
            <b-breadcrumb-item active>
              CYBERBIZ 訂單查詢 
            </b-breadcrumb-item>
          </b-breadcrumb>
        </div>
      </div>
    </div>

    <b-card>
      <div class="row mb-2 align-items-end">
        <div class="col-8 d-flex items-center">
          <h4 class="font-weight-bold">CYBERBIZ 訂單查詢</h4>
        </div>
      </div>

      <b-overlay
        :show="isLoading"
        rounded
        opacity="0.6"
        spinner-small
        spinner-variant="primary"
      >
        <b-form-group
          label-cols="12"
          label-cols-lg="2"
          label-size="sm"
          label="品牌"
        >
          <b-form-select
            v-model="selectedMerchantId"
            :options="merchantOptions"
            :disabled="isFetchingMerchants"
          >
            <template #first>
              <b-form-select-option :value="null" disabled>
                {{ isFetchingMerchants ? '載入中...' : '請選擇品牌' }}
              </b-form-select-option>
            </template>
          </b-form-select>
        </b-form-group>

        <b-form-group
          label-cols="12"
          label-cols-lg="2"
          label-size="sm"
          label="查詢類型"
        >
          <b-form-radio-group
            v-model="searchType"
            :options="searchTypeOptions"
            name="search-type"
          ></b-form-radio-group>
        </b-form-group>

        <b-form-group
          label-cols="12"
          label-cols-lg="2"
          label-size="sm"
          label="訂單查詢"
        >
          <b-input-group>
            <b-form-input
              class="mb-2 mr-sm-2 mb-sm-0"
              type="text"
              v-model="searchQuery"
              :placeholder="searchPlaceholder"
              @keyup.enter="handleSearch"
            ></b-form-input>
            <b-input-group-append>
              <b-button
                class="mr-2"
                variant="info"
                @click="handleSearch"
                :disabled="isLoading || !searchQuery"
              >
              查詢 
              </b-button>
            </b-input-group-append>
          </b-input-group> 
        </b-form-group>
        <editor
          class="mb-2 mr-sm-2 mb-sm-0"
          v-model="result"
          width="100%"
          height="400px"
          @init="editorInit"
          lang="json"
          theme="chrome"
          :disabled="!editable"
        >
        </editor>
      </b-overlay>
    </b-card>
  </div>
</template>

<style scoped lang="scss">
.group + .group {
  border-top: 1px solid #cdd6dc !important;
}
</style>
<script>
import { mapState } from "vuex";
import * as consts from "@/consts";
import PermissionChecker from "@/utils/PermissionChecker";
import editor from "vue2-ace-editor";
import cyberbizApi from "@/apis/cyberbiz";

export default {
  components: { editor },
  data() {
    return {
      searchQuery: null,
      result: "",
      isLoading: false,
      isSubmmiting: false,
      canEdit: false,
      // Merchants dropdown
      merchants: [],
      selectedMerchantId: null,
      isFetchingMerchants: false,
      // Search type radio
      searchType: 'order_id',
      searchTypeOptions: [
        { text: '訂單ID', value: 'order_id' },
        { text: '訂單編號', value: 'order_name' },
      ],
    };
  },
  computed: {
    ...mapState("general", {
      organization: (state) => state.organization,
    }),
    orgId() {
      return this.organization.id;
    },
    editable() {
      return this.canEdit;
    },
    merchantOptions() {
      return this.merchants.map((m) => ({
        value: m.merchant_id,
        text: m.merchant_name,
      }));
    },
    searchPlaceholder() {
      return this.searchType === 'order_name'
        ? '請輸入訂單編號（多筆以逗號分隔）Ex. 1022 或 1022,1021'
        : '請輸入訂單 ID（僅支援單筆）Ex. 45688986';
    },
  },
  watch: {
    searchType() {
      this.searchQuery = "";
      this.result = ""; 
    },
  },
  mounted() {
    if (!this.checkPermission([consts.CYBERBIZ_SETTING_ORDER_QUERY])) {
      this.$swal
        .fire({
          type: "error",
          text: "你沒有權限訪問此頁面",
          confirmButtonColor: "#d33",
        })
        .then(() => {
          this.$router.push({
            name: "DashboardHome",
          });
        });
      return;
    }
    this.fetchMerchants();
  },
  methods: {
    editorInit() {
      require("brace/ext/language_tools"); //language extension prerequsite...
      require("brace/mode/json");
      require("brace/mode/html");
      require("brace/theme/chrome");
    },
    async fetchMerchants() {
      try {
        this.isFetchingMerchants = true;
        const { data } = await cyberbizApi.getMerchants(this.orgId);
        this.merchants = data.data || [];
        // 如果只有一個品牌，自動選取
        if (this.merchants.length === 1) {
          this.selectedMerchantId = this.merchants[0].merchant_id;
        }
      } catch (error) {
        console.error("Failed to fetch merchants:", error);
        this.$swal.fire({
          type: "error",
          text: "載入品牌列表失敗",
        });
      } finally {
        this.isFetchingMerchants = false;
      }
    },
    async handleSearch() {
      const query = this.searchQuery?.trim();

      // 驗證：必須選擇品牌
      if (!this.selectedMerchantId) {
        this.$swal.fire({
          type: "warning",
          text: "請先選擇品牌",
        });
        return;
      }

      // 驗證：必須輸入查詢條件
      if (!query) {
        this.$swal.fire({
          type: "warning",
          text: this.searchType === 'order_name' ? "請輸入訂單編號" : "請輸入訂單 ID",
        });
        return;
      }

      // 驗證輸入格式
      if (this.searchType === 'order_name') {
        // 訂單編號：允許數字和逗號（多筆情況）
        if (!/^[\d,]+$/.test(query) || /^,|,$|,,/.test(query)) {
          this.$swal.fire({
            type: "warning",
            text: "請輸入有效的訂單編號（僅限數字，多筆以逗號分隔）",
          });
          return;
        }
      } else {
        // 訂單 ID：僅限單一數字
        if (!/^\d+$/.test(query)) {
          this.$swal.fire({
            type: "warning",
            text: "請輸入有效的訂單 ID（僅限數字，不支援多筆）",
          });
          return;
        }
      }

      try {
        this.isLoading = true;
        this.isSubmmiting = true;
        this.result = "查詢中...";

        const params = {
          merchant_id: this.selectedMerchantId,
        };

        if (this.searchType === 'order_name') {
          params.order_numbers = query;
        } else {
          params.order_id = query;
        }

        const result = await cyberbizApi.searchOrder(this.orgId, params);
        this.result = JSON.stringify(result.data, null, 2);
      } catch (error) {
        console.error("Order search failed:", error);
        if (error.response && error.response.data) {
          this.result = JSON.stringify(error.response.data, null, 2);
        } else {
          this.result = JSON.stringify({
            error: "查詢失敗",
            message: error.message || "未知錯誤"
          }, null, 2);
        }
      } finally {
        this.isLoading = false;
        this.isSubmmiting = false;
      }
    },
    checkPermission(permissions) {
      const checker = new PermissionChecker();
      return checker.check(permissions);
    },
  },
};
</script>
